import {nonEmptyString, nonEmptyStringOrNull, notNull, assertString} from "./assert";
import {FormRegistration, registerTurnstileForCustomSubmitForm, TURNSTILE_RESPONSE_FIELD} from "./cloudflare_turnstile/cloudflareTurnstile";
import {
    SlAlert,
    SlButton,
    SlCheckbox,
    SlRadioGroup,
    SlInput,
    SlSelect,
} from "@shoelace-style/shoelace/shoelace.js";
import {CSRF_TOKEN_HEADER_NAME, CSRF_TOKEN_FORM_FIELD_NAME} from "./common/csrf";

export class ClxFormIntegration extends HTMLElement {
    private readonly sid: string;
    private readonly profileUid: string|null;
    private readonly form: HTMLFormElement;
    private readonly turnstileForm: FormRegistration;
    private readonly successAlert: SlAlert;
    private readonly successList: HTMLUListElement;
    private readonly errorAlert: SlAlert;
    private readonly errorTitle: HTMLElement;
    private readonly errorList: HTMLUListElement;
    private readonly config: Config;
    private readonly fieldElements: FieldElements;

    public constructor() {
        super();

        this.sid = nonEmptyString(this.getAttribute('sid'));
        this.profileUid = nonEmptyStringOrNull(this.getAttribute('profile-uid'));
        this.form = notNull(this.querySelector<HTMLFormElement>('form'));
        this.successAlert = notNull(this.querySelector<SlAlert>('sl-alert.formIntegration__success-alert'));
        this.successList = notNull(this.querySelector<HTMLUListElement>('ul.formIntegration__success-list'));
        this.errorAlert = notNull(this.querySelector<SlAlert>('sl-alert.formIntegration__error-alert'));
        this.errorTitle = notNull(this.querySelector<HTMLElement>('.formIntegration__error-title'));
        this.errorList = notNull(this.querySelector<HTMLUListElement>('ul.formIntegration__error-list'));

        const configElement = notNull(this.querySelector<HTMLScriptElement>('.formIntegration__config'));
        this.config = readFieldConfig(configElement);

        this.fieldElements = readFieldElements(this.config.fields, this.form);

        this.turnstileForm = registerTurnstileForCustomSubmitForm(this.form);

        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.form.querySelectorAll('sl-input').forEach(input => {
            input.addEventListener('keydown', e => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                }
            });
        });

        this.clearCustomValidityOnInput();
        initializeNewsletterCheckboxes(this);
    }

    public async onSubmit(e: SubmitEvent) {
        e.preventDefault();

        if (! this.turnstileForm.hasResponse()) {
            return;
        }

        this.setSubmitting(true);
        this.clearError();
        try {
            const { headers, payload } = this.collectHeadersAndPayload();
            let response;
            try {
                response = await fetch(this.form.action, {
                    method: this.form.method,
                    headers: {
                        'Content-Type': 'application/json',
                        ...headers,
                    },
                    body: JSON.stringify(payload),
                });
            } catch (e) {
                await this.showError();
                return;
            }

            if (! response.ok) {
                await this.showError();
                return;
            }

            let submitResult: SubmitResult;
            try {
                submitResult = await response.json();
            } catch (e) {
                await this.showError();
                return;
            }

            if (! submitResult.ok) {
                await this.showError(submitResult);
                return;
            }

            this.showSuccess(submitResult);
        }  finally {
            this.setSubmitting(false);
        }
    }

    private setSubmitting(submitting: boolean) {
        this.querySubmitButtons().forEach(button => {
            if (submitting) {
                button.disabled = true;
            } else {
                button.disabled = ! this.turnstileForm.hasResponse();
            }
            button.loading = submitting;
        });
    }

    private clearError() {
        this.errorList.innerHTML = '';
        this.errorAlert.open = false;
    }

    private async showError(error: SubmitError|undefined = undefined) {
        this.resetCustomValidity();

        let errorListElements: HTMLLIElement[] = [];
        if (error !== undefined) {
            errorListElements = error.invalidFieldNames.map(fieldName => {
                const li = document.createElement('li');
                li.textContent = this.config.fields[fieldName]?.label ?? fieldName;
                return li;
            });
            await this.applyCustomValidity(error.invalidFieldNames);
            this.form.reportValidity();
        }

        this.form.classList.add('form-validated');

        this.errorList.innerHTML = '';
        this.errorList.append(...errorListElements);

        this.errorTitle.textContent = error?.message ?? this.config.translations.error_generic;
        this.errorAlert.open = true;

        await this.errorAlert.updateComplete;

        this.scrollToTop();

        console.log(new Date(), 'turnstile reset');
        this.turnstileForm.reset().then(() => console.log(new Date(), 'turnstile resetted'));
    }

    private showSuccess(submitSuccess: SubmitSuccess) {
        const messages = [...submitSuccess.successMessages, ...submitSuccess.warningMessages];
        if (messages.length > 0) {
            this.successList.append(
                ...messages.map(message => {
                    const li = document.createElement('li');
                    li.textContent = message;
                    return li;
                }),
            );
        }

        this.clearError();
        this.form.ariaHidden = 'true';
        this.form.style.display = 'none';
        this.successAlert.open = true;
    }

    private collectHeadersAndPayload() {
        const data = this.collectFormData();
        const cfTurnstileResponse = data[TURNSTILE_RESPONSE_FIELD];
        assertString(cfTurnstileResponse);
        delete data[TURNSTILE_RESPONSE_FIELD];

        const csrfTokenValue = data[CSRF_TOKEN_FORM_FIELD_NAME];
        assertString(csrfTokenValue);
        delete data[CSRF_TOKEN_FORM_FIELD_NAME];

        const payload = {
            sid: this.sid,
            profileUid: this.profileUid,
            [TURNSTILE_RESPONSE_FIELD]: cfTurnstileResponse,
            data,
        };

        const headers = {
            [CSRF_TOKEN_HEADER_NAME]: csrfTokenValue,
        } satisfies Record<string, string>;

        return { headers, payload };
    }

    private collectFormData() {
        const formData = new FormData(this.form);
        const data: Record<string, string|string[]> = {};
        for (const key of formData.keys()) {
            const values = formData
                .getAll(key)
                .filter(v => typeof v === 'string');
            data[key] = this.collectFieldData(key, values);
        }
        return data;
    }

    private collectFieldData(name: string, values: string[]): string|string[] {
        if (values.length > 1 || this.isMultiValueField(name)) {
            return values;
        } else {
            return values[0];
        }
    }

    private isMultiValueField(name: string) {
        const fieldConfig = this.config.fields[name] ?? null;
        return fieldConfig !== null && fieldConfig.multiValue;
    }

    private async applyCustomValidity(invalidFieldNames: string[]): Promise<void>
    {
        const waitList = [];
        for (const invalidFieldName of invalidFieldNames) {
            for (const element of this.fieldElements[invalidFieldName] ?? []) {
                element.setCustomValidity(this.config.translations.error_field_generic);
                waitList.push(element.updateComplete);            }
        }
        await Promise.all(waitList);
    }

    private resetCustomValidity(): void
    {
        for (const elements of Object.values(this.fieldElements)) {
            for (const element of elements) {
                element.setCustomValidity('');
            }
        }
    }

    private clearCustomValidityOnInput() {
        for (const elements of Object.values(this.fieldElements)) {
            for (const element of elements) {
                element.addEventListener('sl-input', () => {
                    element.setCustomValidity('');
                    const elementsWithSameName = this.fieldElements[element.name];
                    for (const elementWithSameName of elementsWithSameName) {
                        elementWithSameName.setCustomValidity('');
                    }
                });
            }
        }
    }

    private scrollToTop() {
        this.scrollIntoView(true);
    }

    private querySubmitButtons() {
        return this.form.querySelectorAll<SlButton>('sl-button[type="submit"]');
    }
}

function readFieldConfig(configElement: HTMLScriptElement): Config {
    const configJson = configElement.textContent;
    if (configJson === null) {
        throw new Error('Config element has no content');
    }
    return JSON.parse(configJson);
}

function readFieldElements(fieldConfig: Record<string, FieldConfig>, form: HTMLFormElement): FieldElements
{
    const fieldElements: FieldElements = {};

    const elements = form.querySelectorAll<SlInput|SlSelect|SlCheckbox|SlRadioGroup>('sl-input,sl-select,sl-checkbox,sl-radio-group');
    for (const element of elements) {
        if (! (element.name in fieldConfig)) {
            continue;
        }
        if (! (element.name in fieldElements)) {
            fieldElements[element.name] = [];
        }
        fieldElements[element.name].push(element);
    }

    return fieldElements;
}

type SubmitSuccess = {
    ok: true,
    successMessages: string[],
    warningMessages: string[],
};

type SubmitError = {
    ok: false,
    message: string,
    invalidFieldNames: string[],
};

type SubmitResult = SubmitSuccess | SubmitError;

type FieldElements = Record<string, Array<SlInput|SlSelect|SlCheckbox|SlRadioGroup>>;

type FieldConfig = {
    name: string,
    label: string,
    multiValue: boolean,
};

type Config = {
    fields: Record<string, FieldConfig>,
    translations: {
        error_generic: string,
        error_field_generic: string,
    },
};

function initializeNewsletterCheckboxes(parent: HTMLElement): void
{
    const newsletterRegistrationCheckbox = parent
        .querySelector<SlCheckbox>('sl-checkbox[name="newsletterRegistrationField"]');
    const newsletterTrackingRow = parent
        .querySelector<SlCheckbox>('sl-checkbox[name="newsletterTrackingField"]')
        ?.closest<HTMLElement>('.formIntegration__row')
        ?? null;
    if (newsletterRegistrationCheckbox !== null && newsletterTrackingRow !== null) {
        newsletterRegistrationCheckbox.addEventListener('sl-change', () => {
            newsletterTrackingRow.classList.toggle('hidden', ! newsletterRegistrationCheckbox.checked);
        });
    }
}

if (! customElements.get('clx-form-integration')) {
    customElements.define('clx-form-integration', ClxFormIntegration);
}
